import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export interface RunBuildPreset {
  projectSlug?: string;
  appId?: string;
  profileId?: string;
  environment?: string;
}

interface RunBuildState {
  open: boolean;
  preset: RunBuildPreset;
  openRunBuild: (preset?: RunBuildPreset) => void;
  closeRunBuild: () => void;
}

const Ctx = createContext<RunBuildState | null>(null);

/** One Run build sheet for the whole shell (top bar button, phone FAB, Builds page, profiles "Run"). */
export function RunBuildProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<RunBuildPreset>({});
  // The sheet is opened from state (not a Radix trigger), so focus is handed back to the opener by hand.
  const opener = useRef<HTMLElement | null>(null);
  const openRunBuild = useCallback((p: RunBuildPreset = {}) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPreset(p);
    setOpen(true);
  }, []);
  const closeRunBuild = useCallback(() => {
    setOpen(false);
    const el = opener.current;
    opener.current = null;
    if (el && el.isConnected) requestAnimationFrame(() => el.focus({ preventScroll: true }));
  }, []);
  const value = useMemo(() => ({ open, preset, openRunBuild, closeRunBuild }), [open, preset, openRunBuild, closeRunBuild]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRunBuild(): RunBuildState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRunBuild outside RunBuildProvider');
  return v;
}
